/**
 * useCurrentRole — fetches the current user's role in the active workspace.
 *
 * Phase 30.8 (Budget B1): introduced for role-gated edit of the project
 * Approved Budget field. Generic enough to be used elsewhere; expect more
 * callers in later phases (B2's line-item CRUD, etc.).
 *
 * Returns:
 *   - 'owner' | 'admin' | 'member' — when known
 *   - null  — while loading, or when no workspace is active, or when not
 *             signed in. UI should treat null as "no permissions yet."
 *
 * Design notes:
 *   - In localStorage mode (no Supabase), we don't have org membership and
 *     return null. Callers should treat that as "ok to edit" since there's
 *     no multi-user context — the localStorage mode is single-user only.
 *     We expose this distinction via the `mode` field below.
 *   - Cached per-org for the lifetime of this hook's component. A workspace
 *     switch fetches fresh. There's no cache invalidation on membership
 *     change (e.g. if the user is demoted while looking at the page) — that
 *     would require realtime wiring, which is overkill for v1. The worst
 *     case is a stale "you can edit" indication that the server-side RLS
 *     will correctly reject on save.
 */

import { useEffect, useState } from 'react';
import { useAuthStore } from '@/store/useAuthStore';
import { useProjectStore } from '@/store/useProjectStore';
import { listMembers } from '@/lib/api';

export type WorkspaceRole = 'owner' | 'admin' | 'member';

export interface CurrentRoleState {
  role: WorkspaceRole | null;
  loading: boolean;
  /** 'api' = real workspace from Supabase, 'local' = localStorage mode */
  mode: 'api' | 'local';
}

export function useCurrentRole(): CurrentRoleState {
  const user = useAuthStore((s) => s.user);
  const mode = useProjectStore((s) => s.mode);
  const activeOrgId = useProjectStore((s) => s.activeOrgId);

  const [role, setRole] = useState<WorkspaceRole | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    // localStorage mode: no role concept. Treat as null+not-loading; the
    // mode field tells callers it's a single-user environment.
    if (mode !== 'api') {
      setRole(null);
      setLoading(false);
      return;
    }
    if (!user || !activeOrgId) {
      setRole(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await listMembers(activeOrgId);
      if (cancelled) return;
      if (res.error || !res.data) {
        setRole(null);
        setLoading(false);
        return;
      }
      const me = res.data.find((m) => m.user_id === user.id);
      setRole((me?.role as WorkspaceRole | undefined) ?? null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, activeOrgId, mode]);

  return { role, loading, mode: mode === 'api' ? 'api' : 'local' };
}

/**
 * Convenience: returns true when the current user can perform an
 * Owner-or-Admin action. In localStorage mode this is always true
 * (single-user environment, no roles).
 */
export function useCanManage(): boolean {
  const { role, mode } = useCurrentRole();
  if (mode !== 'api') return true;
  return role === 'owner' || role === 'admin';
}
